import React, { useEffect, useRef, useState } from 'react';
import { useLanguage, Language } from '../hooks/useLanguage';
import { ChevronDownIcon } from './Icons';
import './LanguageSwitcher.css';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'footer';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'default',
  className = ''
}) => {
  const { currentLanguage, switchLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang: Language) => {
    if (lang !== currentLanguage) {
      switchLanguage(lang);
    }
    setIsOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div className={`language-switcher language-switcher--compact ${className}`}>
        <button
          type="button"
          className={`language-btn ${currentLanguage === 'pt' ? 'active' : ''}`}
          onClick={() => handleLanguageChange('pt')}
          aria-label="Português"
        >
          PT
        </button>
        <span className="language-divider">|</span>
        <button
          type="button"
          className={`language-btn ${currentLanguage === 'en' ? 'active' : ''}`}
          onClick={() => handleLanguageChange('en')}
          aria-label="English"
        >
          EN
        </button>
      </div>
    );
  }

  if (variant === 'footer') {
    const languages: { code: Language; label: string; flag: string }[] = [
      { code: 'pt', label: 'Português', flag: '🇧🇷' },
      { code: 'en', label: 'English', flag: '🌐' },
    ];

    const activeLang = languages.find((l) => l.code === currentLanguage) ?? languages[0];

    return (
      <div
        ref={dropdownRef}
        className={`language-switcher language-switcher--footer ${isOpen ? 'open' : ''} ${className}`}
      >
        <button
          type="button"
          className="language-pill"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label="Select language"
        >
          <div className="language-pill__main">
            <span className="language-pill__icon">🌐</span>
            <span className="language-pill__label">{activeLang.label}</span>
          </div>
          <div className="language-pill__end">
            <span className="language-pill__flag">{activeLang.flag}</span>
            <ChevronDownIcon size={16} className="language-pill__chevron" />
          </div>
        </button>

        <div className={`language-menu ${isOpen ? 'open' : ''}`} role="listbox">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`language-menu__item ${currentLanguage === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              role="option"
              aria-selected={currentLanguage === lang.code}
            >
              <span className="language-menu__flag">{lang.flag}</span>
              <span className="language-menu__label">{lang.label}</span>
              {currentLanguage === lang.code && <span className="language-menu__dot" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`language-switcher ${className}`}>
      <select
        value={currentLanguage}
        onChange={(e) => handleLanguageChange(e.target.value as Language)}
        className="language-select"
        aria-label="Select language"
      >
        <option value="pt">Português (Brasil)</option>
        <option value="en">English</option>
      </select>
    </div>
  );
};

export default LanguageSwitcher;
